import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import firebaseAuthService from '../repositories/firebase/firebaseAuth';
import { Result } from '../models/result';

const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      userToken: null,
      isAuthenticated: false,
      isLoading: false,
      authError: null,

      // Actions
      setSchoolContext: (schoolId) => {
        firebaseAuthService.setSchoolContext(schoolId);
        return new Result({ value: true });
      },

      signOut: async () => {
        try {
          set({ isLoading: true });
          await firebaseAuthService.signOut();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            userToken: null,
            authError: null
          });
          return new Result({ value: true });
        } catch (error) {
          set({ isLoading: false, authError: error.message });
          return new Result({ message: error.message });
        }
      },

      clearError: () => {
        set({ authError: null });
        return new Result({ value: true });
      },

      /**
       * Signs Firebase Auth in with the custom token from the ai-service login.
       * Called by useUserStore.loginUser after userRepo.loginWithPassword succeeds.
       * The ID token it stores is sent by httpRepo on every ai-service request.
       *
       * @param {string} customToken - Firebase custom token from the ai-service
       * @returns {Promise<Result>} Result with the signed-in Firebase user
       */
      signInWithCustomToken: async (customToken) => {
        set({ isLoading: true, authError: null });
        const signInResult = await firebaseAuthService.signInWithCustomToken(customToken);

        if (!signInResult.success) {
          set({ isLoading: false, authError: signInResult.message });
          return signInResult;
        }

        const firebaseUser = signInResult.value;
        const idToken = await firebaseUser.getIdToken();
        set({ user: firebaseUser, isAuthenticated: true, userToken: idToken, isLoading: false });
        return signInResult;
      },

      /**
       * Listens to Firebase Auth and keeps user, userToken and isAuthenticated in step.
       * Started once by App.js for dashboard routes. Resolves after the first auth state.
       * Sessions left over from the old anonymous sign-in are signed out, so those users
       * log in again through useUserStore.loginUser.
       *
       * @returns {Promise<Result>} Result with the unsubscribe function
       */
      initAuthListener: async () => {
        return new Promise((resolve) => {
          const unsubscribe = firebaseAuthService.onAuthStateChanged(async (firebaseUser) => {
            if (firebaseUser && firebaseUser.isAnonymous) {
              await get().signOut();
            } else if (firebaseUser) {
              try {
                const idToken = await firebaseUser.getIdToken();
                set({ user: firebaseUser, isAuthenticated: true, userToken: idToken, authError: null });
              } catch (error) {
                console.error('Failed to get user token:', error);
                set({ authError: error.message });
              }
            } else {
              set({ user: null, isAuthenticated: false, userToken: null });
            }

            // Resolve after first auth state change
            resolve(new Result({ value: unsubscribe }));
          });
        });
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated
      })
    }
  )
);

export default useAuthStore;
